import React, { useState, useEffect, useCallback } from 'react';
import { Target, TrendingUp, Home, DollarSign, HelpCircle, Info, RotateCcw, Save, Loader2, Check, AlertCircle } from 'lucide-react';
import { investmentCriteriaAPI } from '../api';
import { toast } from 'sonner';

// Same tooltips as onboarding
const TOOLTIPS = {
  en: {
    min_net_profit: { title: 'Minimum Net Profit', description: 'The minimum dollar profit you want to make after all expenses.', typical: '$25,000 - $50,000' },
    min_profit_margin: { title: 'Minimum Profit Margin', description: 'Net profit as a percentage of ARV.', typical: '12% - 20%' },
    min_roi: { title: 'Minimum ROI', description: 'Return on Investment - profit divided by total cash invested.', typical: '15% - 30%' },
    min_monthly_cash_flow: { title: 'Minimum Monthly Cash Flow', description: 'Monthly income remaining after all expenses.', typical: '$200 - $500' },
    min_cap_rate: { title: 'Minimum Cap Rate', description: 'Cap Rate measures return before financing.', typical: '6% - 10%' },
    min_cash_on_cash: { title: 'Minimum Cash-on-Cash Return', description: 'Annual cash flow divided by total cash invested.', typical: '8% - 15%' },
    min_dscr: { title: 'Minimum DSCR', description: 'Debt Service Coverage Ratio - NOI divided by debt payments.', typical: '1.2 - 1.5' },
    min_cash_flow_after_refi: { title: 'Min Cash Flow After Refinance', description: 'Monthly cash flow after refinancing.', typical: '$150 - $300' },
    max_cash_left_in_deal: { title: 'Maximum Cash Left in Deal', description: 'Investment remaining after refinancing.', typical: '$0 - $30,000' },
    target_refi_ltv: { title: 'Target Refinance LTV', description: 'Loan-to-Value at refinance.', typical: '70% - 80%' }
  },
  es: {
    min_net_profit: { title: 'Ganancia Neta Mínima', description: 'La ganancia mínima después de gastos.', typical: '$25,000 - $50,000' },
    min_profit_margin: { title: 'Margen de Ganancia Mínimo', description: 'Ganancia neta como porcentaje del ARV.', typical: '12% - 20%' },
    min_roi: { title: 'ROI Mínimo', description: 'Retorno sobre la Inversión.', typical: '15% - 30%' },
    min_monthly_cash_flow: { title: 'Flujo de Caja Mensual Mínimo', description: 'Ingreso mensual después de gastos.', typical: '$200 - $500' },
    min_cap_rate: { title: 'Cap Rate Mínimo', description: 'El Cap Rate mide el retorno antes del financiamiento.', typical: '6% - 10%' },
    min_cash_on_cash: { title: 'Retorno Cash-on-Cash Mínimo', description: 'Flujo de caja anual dividido por inversión.', typical: '8% - 15%' },
    min_dscr: { title: 'DSCR Mínimo', description: 'Ratio de Cobertura de Servicio de Deuda.', typical: '1.2 - 1.5' },
    min_cash_flow_after_refi: { title: 'Flujo Después de Refinanciar', description: 'Flujo mensual después de refinanciar.', typical: '$150 - $300' },
    max_cash_left_in_deal: { title: 'Máximo Efectivo en el Trato', description: 'Inversión restante después de refinanciar.', typical: '$0 - $30,000' },
    target_refi_ltv: { title: 'LTV Objetivo de Refinanciamiento', description: 'Loan-to-Value al refinanciar.', typical: '70% - 80%' }
  }
};

const TooltipButton = ({ field, lang }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const tooltips = TOOLTIPS[lang] || TOOLTIPS.en;
  const tooltip = tooltips[field];
  
  if (!tooltip) return null;
  
  return (
    <div className="relative inline-block">
      <button
        type="button"
        className="text-slate-400 hover:text-blue-500 transition-colors ml-1"
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onClick={() => setShowTooltip(!showTooltip)}
      >
        <HelpCircle className="w-4 h-4" />
      </button>
      {showTooltip && (
        <div className="absolute z-50 left-6 top-0 w-56 p-2.5 bg-slate-800 text-white text-xs rounded-lg shadow-xl">
          <div className="font-semibold mb-1">{tooltip.title}</div>
          <div className="text-slate-300 mb-1">{tooltip.description}</div>
          <div className="text-blue-300 text-[10px]">
            {lang === 'es' ? 'Típico:' : 'Typical:'} {tooltip.typical}
          </div>
          <div className="absolute left-[-6px] top-2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[6px] border-r-slate-800"></div>
        </div>
      )}
    </div>
  );
};

const CriteriaInput = ({ label, field, value, defaultValue, onChange, prefix = '', suffix = '', lang, isDefault }) => {
  return (
    <div className="mb-3">
      <label className="flex items-center text-sm font-medium text-slate-700 mb-1">
        {label}
        <TooltipButton field={field} lang={lang} />
        {isDefault && (
          <span className="ml-2 text-xs text-slate-400">({lang === 'es' ? 'predeterminado' : 'default'})</span>
        )}
      </label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{prefix}</span>
        )}
        <input
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value ? parseFloat(e.target.value) : null)}
          placeholder={defaultValue?.toString()}
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm ${
            prefix ? 'pl-7' : ''
          } ${suffix ? 'pr-8' : ''} ${
            isDefault ? 'border-slate-200 bg-slate-50 text-slate-500' : 'border-slate-300 bg-white'
          }`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">{suffix}</span>
        )}
      </div>
    </div>
  );
};

const InvestmentCriteriaSettings = ({ lang = 'en', onCriteriaChange }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  
  const [criteria, setCriteria] = useState(null);
  const [rawCriteria, setRawCriteria] = useState(null);
  const [defaults, setDefaults] = useState(null);

  const loadCriteria = useCallback(async () => {
    try {
      setLoading(true);
      const response = await investmentCriteriaAPI.getCriteria();
      setCriteria(response.data.criteria);
      setRawCriteria(response.data.raw_criteria || {});
      setDefaults(response.data.defaults);
    } catch (error) {
      console.error('Failed to load criteria:', error);
      toast.error(lang === 'en' ? 'Failed to load criteria' : 'Error al cargar criterios');
    } finally {
      setLoading(false);
    }
  }, [lang]);

  useEffect(() => {
    loadCriteria();
  }, [loadCriteria]);

  const updateCriteria = (category, field, value) => {
    setRawCriteria(prev => ({
      ...prev,
      [category]: {
        ...(prev[category] || {}),
        [field]: value
      }
    }));
    setCriteria(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value ?? defaults[category][field]
      }
    }));
    setHasUnsavedChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await investmentCriteriaAPI.updateCriteria(rawCriteria);
      setCriteria(response.data.criteria);
      setHasUnsavedChanges(false);
      toast.success(lang === 'en' ? 'Investment criteria saved!' : '¡Criterios guardados!');
      onCriteriaChange?.(response.data.criteria);
    } catch (error) {
      console.error('Failed to save criteria:', error);
      toast.error(lang === 'en' ? 'Failed to save criteria' : 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(lang === 'en' 
      ? 'Reset all criteria to recommended defaults?' 
      : '¿Restablecer todos los criterios a los valores predeterminados?'
    )) return;
    
    setResetting(true);
    try {
      const response = await investmentCriteriaAPI.resetCriteria();
      setCriteria(response.data.criteria);
      setRawCriteria({ flip: {}, rental: {}, brrrr: {} });
      setHasUnsavedChanges(false);
      toast.success(lang === 'en' ? 'Criteria reset to defaults' : 'Criterios restablecidos');
      onCriteriaChange?.(response.data.criteria);
    } catch (error) {
      console.error('Failed to reset criteria:', error);
      toast.error(lang === 'en' ? 'Failed to reset criteria' : 'Error al restablecer');
    } finally {
      setResetting(false);
    }
  };

  const isUsingDefault = (category, field) => {
    return rawCriteria?.[category]?.[field] == null;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-testid="investment-criteria-settings">
      {/* Header */}
      <div className="p-4 border-b border-slate-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Target className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-800">
                {lang === 'es' ? 'Criterios de Inversión' : 'Investment Criteria'}
              </h3>
              <p className="text-sm text-slate-500">
                {lang === 'es' 
                  ? 'Define tus requisitos mínimos para recomendaciones personalizadas' 
                  : 'Define your minimum requirements for personalized recommendations'}
              </p>
            </div>
          </div>
          {hasUnsavedChanges && (
            <span className="flex items-center gap-1 text-sm text-amber-600">
              <AlertCircle className="w-4 h-4" />
              {lang === 'es' ? 'Sin guardar' : 'Unsaved'}
            </span>
          )}
        </div>
      </div>

      {/* Info Banner */}
      <div className="m-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
        <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-blue-700">
          {lang === 'es'
            ? 'Estos criterios afectan las recomendaciones PASA/NO PASA. Los campos vacíos usan valores predeterminados.'
            : 'These criteria affect PASS/NO PASS recommendations. Empty fields use default values.'}
        </p>
      </div>

      {/* Content Grid */}
      <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Fix & Flip */}
        <div className="space-y-2">
          <h4 className="flex items-center gap-2 font-semibold text-slate-800 pb-2 border-b border-slate-200">
            <TrendingUp className="w-4 h-4 text-blue-600" />
            Fix & Flip
          </h4>
          <CriteriaInput
            label={lang === 'es' ? 'Ganancia Neta Mín' : 'Min Net Profit'}
            field="min_net_profit"
            value={rawCriteria?.flip?.min_net_profit}
            defaultValue={defaults?.flip?.min_net_profit}
            onChange={(v) => updateCriteria('flip', 'min_net_profit', v)}
            prefix="$"
            lang={lang}
            isDefault={isUsingDefault('flip', 'min_net_profit')}
          />
          <CriteriaInput
            label={lang === 'es' ? 'Margen Mín' : 'Min Margin'}
            field="min_profit_margin"
            value={rawCriteria?.flip?.min_profit_margin}
            defaultValue={defaults?.flip?.min_profit_margin}
            onChange={(v) => updateCriteria('flip', 'min_profit_margin', v)}
            suffix="%"
            lang={lang}
            isDefault={isUsingDefault('flip', 'min_profit_margin')}
          />
          <CriteriaInput
            label={lang === 'es' ? 'ROI Mín' : 'Min ROI'}
            field="min_roi"
            value={rawCriteria?.flip?.min_roi}
            defaultValue={defaults?.flip?.min_roi}
            onChange={(v) => updateCriteria('flip', 'min_roi', v)}
            suffix="%"
            lang={lang}
            isDefault={isUsingDefault('flip', 'min_roi')}
          />
        </div>

        {/* Rental */}
        <div className="space-y-2">
          <h4 className="flex items-center gap-2 font-semibold text-slate-800 pb-2 border-b border-slate-200">
            <Home className="w-4 h-4 text-green-600" />
            {lang === 'es' ? 'Alquiler' : 'Rental'}
          </h4>
          <CriteriaInput
            label={lang === 'es' ? 'Flujo Mensual Mín' : 'Min Cash Flow'}
            field="min_monthly_cash_flow"
            value={rawCriteria?.rental?.min_monthly_cash_flow}
            defaultValue={defaults?.rental?.min_monthly_cash_flow}
            onChange={(v) => updateCriteria('rental', 'min_monthly_cash_flow', v)}
            prefix="$"
            lang={lang}
            isDefault={isUsingDefault('rental', 'min_monthly_cash_flow')}
          />
          <CriteriaInput
            label={lang === 'es' ? 'Cap Rate Mín' : 'Min Cap Rate'}
            field="min_cap_rate"
            value={rawCriteria?.rental?.min_cap_rate}
            defaultValue={defaults?.rental?.min_cap_rate}
            onChange={(v) => updateCriteria('rental', 'min_cap_rate', v)}
            suffix="%"
            lang={lang}
            isDefault={isUsingDefault('rental', 'min_cap_rate')}
          />
          <CriteriaInput
            label={lang === 'es' ? 'Cash-on-Cash Mín' : 'Min CoC'}
            field="min_cash_on_cash"
            value={rawCriteria?.rental?.min_cash_on_cash}
            defaultValue={defaults?.rental?.min_cash_on_cash}
            onChange={(v) => updateCriteria('rental', 'min_cash_on_cash', v)}
            suffix="%"
            lang={lang}
            isDefault={isUsingDefault('rental', 'min_cash_on_cash')}
          />
          <CriteriaInput
            label={lang === 'es' ? 'DSCR Mín' : 'Min DSCR'}
            field="min_dscr"
            value={rawCriteria?.rental?.min_dscr}
            defaultValue={defaults?.rental?.min_dscr}
            onChange={(v) => updateCriteria('rental', 'min_dscr', v)}
            lang={lang}
            isDefault={isUsingDefault('rental', 'min_dscr')}
          />
        </div>

        {/* BRRRR */}
        <div className="space-y-2">
          <h4 className="flex items-center gap-2 font-semibold text-slate-800 pb-2 border-b border-slate-200">
            <DollarSign className="w-4 h-4 text-purple-600" />
            BRRRR
          </h4>
          <CriteriaInput
            label={lang === 'es' ? 'Flujo Post-Refi' : 'Post-Refi Flow'}
            field="min_cash_flow_after_refi"
            value={rawCriteria?.brrrr?.min_cash_flow_after_refi}
            defaultValue={defaults?.brrrr?.min_cash_flow_after_refi}
            onChange={(v) => updateCriteria('brrrr', 'min_cash_flow_after_refi', v)}
            prefix="$"
            lang={lang}
            isDefault={isUsingDefault('brrrr', 'min_cash_flow_after_refi')}
          />
          <CriteriaInput
            label={lang === 'es' ? 'Máx Efectivo en Trato' : 'Max Cash Left'}
            field="max_cash_left_in_deal"
            value={rawCriteria?.brrrr?.max_cash_left_in_deal}
            defaultValue={defaults?.brrrr?.max_cash_left_in_deal}
            onChange={(v) => updateCriteria('brrrr', 'max_cash_left_in_deal', v)}
            prefix="$"
            lang={lang}
            isDefault={isUsingDefault('brrrr', 'max_cash_left_in_deal')}
          />
          <CriteriaInput
            label={lang === 'es' ? 'LTV Objetivo' : 'Target LTV'}
            field="target_refi_ltv"
            value={rawCriteria?.brrrr?.target_refi_ltv}
            defaultValue={defaults?.brrrr?.target_refi_ltv}
            onChange={(v) => updateCriteria('brrrr', 'target_refi_ltv', v)}
            suffix="%"
            lang={lang}
            isDefault={isUsingDefault('brrrr', 'target_refi_ltv')}
          />
        </div>
      </div>

      {/* Footer Actions */}
      <div className="p-4 border-t border-slate-200 bg-slate-50 flex items-center justify-between">
        <button
          onClick={handleReset}
          disabled={resetting}
          className="flex items-center gap-2 px-4 py-2 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-200 rounded-lg transition-colors disabled:opacity-50"
        >
          {resetting ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <RotateCcw className="w-4 h-4" />
          )}
          {lang === 'es' ? 'Restablecer' : 'Reset to Defaults'}
        </button>
        <button
          onClick={handleSave}
          disabled={saving || !hasUnsavedChanges}
          className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : hasUnsavedChanges ? (
            <Save className="w-4 h-4" />
          ) : (
            <Check className="w-4 h-4" />
          )}
          {saving 
            ? (lang === 'es' ? 'Guardando...' : 'Saving...') 
            : hasUnsavedChanges 
              ? (lang === 'es' ? 'Guardar Cambios' : 'Save Changes')
              : (lang === 'es' ? 'Guardado' : 'Saved')}
        </button>
      </div>
    </div>
  );
};

export default InvestmentCriteriaSettings;
